import React, { FC, useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { RootState } from '../../../../states/containers/store'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { cloneDeep, filter, sortBy, uniqueId } from 'lodash'
import Tips from '../../../components/common/Tips'
import ConfirmationModal from '../../../components/task/ConfirmationModal'
import AddExcludeMemberModal from '../../../components/task/AddExcludeMemberModal'
import {
  createTaskExcludeGenerateMemberSetting,
  destroyTaskExcludeGenerateMemberSetting,
  fetchTaskExcludeGenerateMemberSetting,
} from '../../../../states/containers/task/taskSlice'
import { useAlert } from 'react-alert'
import { format } from '../../../../utils/date'
import { compile } from '../../../../utils/search'

interface ConfirmationModalState {
  display: boolean
  type: string
  name: string
  callback: VoidFunction
}

interface List {
  member_id: number
  name: string
  kana: string | null
  code: string | null
  image_url: string | null
  created_at: string
}

const Index: FC = () => {
  const {
    task,
  } = useSelector((state: RootState) => state)

  const location = useLocation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const [sortLabel, setSortLabel] = useState<string>("")
  const [data, setData] = useState<List[]>([])
  const [dataInit, setDataInit] = useState<List[]>([])
  const [isDisplay, setIsDisplay] = useState<boolean>(false)
  const [searchWord, setSearchWord] = useState<string>("")

  const [confirmationModalState, setConfirmationModalState] = useState<ConfirmationModalState>({
    display: false,
    type: "",
    name: "",
    callback: () => {
      // none
    },
  })

  const sortDate = (dataList: List[]) => {
    const [key, order] = sortLabel.split("@")
    if (!key || !order) {
      setData(sortBy(dataList || [], 'member_id'))
      return
    }
    if (order === "asc") {
      setData(sortBy(dataList || [], key))
    } else if (order === "desc") {
      setData(sortBy(dataList || [], key).reverse())
    }
  }

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setData(cloneDeep(dataInit))
      return
    }
    const word = words.toLowerCase().split(/\s+/)
    const list = filter(dataInit, (value) => {
      if (value === null) return false
      return (compile(word).test(value.name.toLowerCase() + value.kana + value.code))
    })
    sortDate(list)
  }

  const reset = () => {
    setSearchWord("")
    sortDate(dataInit)
  }

  const buildSortArea = (type: string) => {
    if (sortLabel === `${type}@asc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel(`${type}@desc`)}
          />
        </>
      )
    }
    if (sortLabel === `${type}@desc`) {
      return (
        <>
          <FontAwesomeIcon
            icon={['fas', 'sort-alpha-down-alt']}
            style={{ cursor: "pointer" }}
            onClick={() => setSortLabel("")}
          />
        </>
      )
    }
    return (
      <>
        <FontAwesomeIcon
          icon={['fas', 'sort']}
          style={{ cursor: "pointer" }}
          onClick={() => setSortLabel(`${type}@asc`)}
        />
      </>
    )
  }

  // confirmation modal
  const openConfirmationModal = (type: string, id: number) => {
    let callback = () => {
      // none
    }
    switch (type) {
      case "exclude_delete":
        callback = () => {
          dispatch(
            destroyTaskExcludeGenerateMemberSetting(
              { id: id.toString() },
              () => { alert.success("作業データを自動生成しないメンバーを解除しました") },
            ),
          )
        }
        break
      default:
        break
    }
    const name = task.taskExcludeGenerateMemberSetting?.full_members.find((v) => v.member_id === Number(id))?.name
    setConfirmationModalState({
      ...confirmationModalState,
      display: true,
      type: type,
      name: name || "",
      callback: callback,
    })
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({
      ...confirmationModalState,
      display: false,
      type: "",
      name: "",
      callback: () => {
        // none
      },
    })
  }

  const openMemberSetting = () => {
    setIsDisplay(true)
  }

  const closeMemberSetting = () => {
    setIsDisplay(false)
  }

  const addExcludeMember = (memberIds: number[]) => {
    dispatch(
      createTaskExcludeGenerateMemberSetting(
        { member_ids: memberIds },
        () => {
          alert.success("作業データを自動生成しないメンバーを登録しました")
        },
      ),
    )
  }

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location])

  useEffect(() => {
    setDataInit(sortBy(task.taskExcludeGenerateMemberSetting?.exclude_members, 'member_id'))
    setData(sortBy(task.taskExcludeGenerateMemberSetting?.exclude_members, 'member_id'))
  }, [task])

  useEffect(() => {
    dispatch(fetchTaskExcludeGenerateMemberSetting())
  }, [])

  useEffect(() => {
    sortDate(data)
  }, [sortLabel])

  return (
    <section className="task_page setting_page">
      <Helmet>
        <title>Qasee - 工数管理 - 設定 - その他の設定 - 作業データを自動生成しないメンバーの設定</title>
      </Helmet>
      <section className="breadcrumb">
        工数管理
        &nbsp;&gt;&nbsp;
        設定
        &nbsp;&gt;&nbsp;
        その他の設定
        &nbsp;&gt;&nbsp;
        作業データを自動生成しないメンバーの設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          作業データを自動生成しないメンバーの設定
        </h1>
        <div className="tools">
          <div className="search_area">
            <input
              type="text"
              placeholder="キーワード検索"
              onChange={(event) => search(event.target.value)}
              value={searchWord}
            />
            {
              searchWord && (
                <button type="button" className="reset" onClick={reset}>
                  <FontAwesomeIcon icon={['far', 'times-circle']} />
                </button>
              )
            }
          </div>
        </div>
      </section>
      <section className="page_description">
        <p style={{ marginBottom: "4px" }}>作業データを自動生成しないメンバーを設定します。</p>
        <p style={{ marginBottom: "4px" }}>
          メンバーに設定されていると、
          <span style={{ color: "#FF0000", fontWeight: "bold" }}>自動的に作業データが作成されません。</span>
          ご注意ください。
        </p>
      </section>
      {
        task.isLoading ? (
          <LoadingSquare>
            <>
              設定を読み込んでいます…
            </>
          </LoadingSquare>
        ) : (
          <>
            <section className="page_contents content-area">
              <div className="content-block">
                <div className="block-header">
                  <div />
                  <div className="button_area">
                    <button
                      type="button"
                      className="button"
                      onClick={openMemberSetting}
                    >
                      <FontAwesomeIcon icon={['fas', 'plus']} />
                      &nbsp;
                      作業データを自動生成しないメンバーを追加する
                    </button>
                  </div>
                </div>
                <div className="block-detail">
                  <div className="exclude_table">
                    {
                      data.length ? (
                        <div className="row_head">
                          <div>
                            編集
                          </div>
                          <div>
                            メンバー
                            {buildSortArea("kana")}
                          </div>
                          <div>
                            除外設定日時
                            {buildSortArea("created_at")}
                          </div>
                        </div>
                      ) : (
                        <div className="no_data">
                          作業データを自動生成しないメンバーはいません
                        </div>
                      )
                    }
                    {
                      data.map((member) => (
                        <div
                          className="row"
                          key={uniqueId()}
                        >
                          <div>
                            <Tips
                              description="作業データを自動生成しないメンバーを解除する"
                              className=""
                              icon={false}
                            >
                              <>
                                <FontAwesomeIcon
                                  icon={['fas', 'minus-circle']}
                                  style={{ cursor: "pointer" }}
                                  onClick={() => openConfirmationModal("exclude_delete", member.member_id)}
                                />
                              </>
                            </Tips>
                          </div>
                          <div>
                            {
                              member.image_url ? (
                                <img alt="profile" src={member.image_url} className="profile_icon" />
                              ) : (
                                <img
                                  alt=""
                                  src={`/static/images/profile/${(Number(member.member_id) || 0) % 12}.jpeg`}
                                  className="profile_icon"
                                />
                              )
                            }
                            <ruby>
                              {member.name}
                              <rt>{member.kana}</rt>
                            </ruby>
                            {
                              !!member.code && (
                                <>
                                  {`(${member.code})`}
                                </>
                              )
                            }
                          </div>
                          <div>
                            {format(member.created_at, "YYYY年M月D日 HH時mm分ss秒")}
                          </div>
                        </div>
                      ))
                    }
                    <div className="row_foot">
                      <div>
                        <Tips
                          description="作業データを自動生成しないメンバーを追加する"
                          className=""
                          icon={false}
                        >
                          <>
                            <FontAwesomeIcon
                              icon={['fas', 'plus-circle']}
                              style={{ cursor: "pointer" }}
                              onClick={openMemberSetting}
                            />
                          </>
                        </Tips>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={openMemberSetting}
                        >
                          <FontAwesomeIcon icon={['fas', 'plus']} />
                          &nbsp;
                          作業データを自動生成しないメンバーを追加する
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </>
        )
      }
      {/* 確認モーダル */}
      <ConfirmationModal
        confirmationModalState={confirmationModalState}
        closeCallback={closeConfirmationModal}
      />
      {/* 設定モーダル */}
      <AddExcludeMemberModal
        isDisplay={isDisplay}
        closeCallback={() => closeMemberSetting()}
        addCallback={(memberIds) => addExcludeMember(memberIds)}
        closeable
      />
    </section>
  )
}

export default Index
